/**
 * AdminInfluencerEarnings — /admin/influencer-earnings
 *
 * Roll-up of paid Stripe conversions attributed to each influencer code.
 * Reads /api/admin/influencer/earnings.
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { TrendingUp, RefreshCw, Users } from 'lucide-react';

const fmtCents = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const AdminInfluencerEarnings = () => {
  const { API } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/influencer/earnings`);
      setRows(data?.rows || []);
      setTotal(data?.total_revenue_cents || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load earnings');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8" data-testid="admin-influencer-earnings-page">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Referrals</div>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground leading-tight mt-1">
              Influencer earnings
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Post-payment Stripe conversions per referral code. Signups are attribution-only.
            </p>
          </div>
          <Button variant="outline" onClick={load} data-testid="refresh-earnings-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-sm font-heading font-black uppercase tracking-widest text-muted-foreground">
              Total attributed revenue
            </CardTitle>
            <CardDescription className="font-heading text-4xl font-black text-foreground" data-testid="total-revenue">
              {fmtCents(total)}
            </CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>By code</CardTitle>
            <CardDescription>Sorted by revenue · {rows.length} codes tracked</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading && <div className="p-6 text-center text-muted-foreground text-sm">Loading…</div>}
            {!loading && rows.length === 0 && (
              <div className="p-8 text-center text-muted-foreground text-sm" data-testid="earnings-empty">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
                No conversions yet.
              </div>
            )}
            {!loading && rows.map((r) => (
              <div key={r.code} className="p-4 border-b border-border last:border-b-0" data-testid={`earnings-row-${r.code}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge className="uppercase tracking-widest text-[10px]">{r.code}</Badge>
                      <span className="text-xs text-muted-foreground truncate">{r.influencer_email || r.influencer_id}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      {r.conversions} conversion{r.conversions === 1 ? '' : 's'} · {r.signups} signup{r.signups === 1 ? '' : 's'}
                      {r.last_event_at && <> · last {new Date(r.last_event_at).toLocaleDateString()}</>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-heading text-lg font-black">{fmtCents(r.revenue_cents)}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Revenue</div>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminInfluencerEarnings;
